import { axiosApiInstance } from "./axios";
import { axiosAuthInstance } from "./axiosAuth";
import { createApiError } from "./apiErrorHandler";
import logger from "../utils/logger";

const base_url = import.meta.env.VITE_API_ENDPOINT;

export const getDistributorBalance = async () => {
  try {
    const url = `${base_url}/distributor/balance`;
    const { data } = await axiosAuthInstance.post(url);
    return data;
  } catch (ex) {
    return createApiError(ex, "getDistributorBalance");
  }
};

export const getDistributorCustomersList = async () => {
  try {
    const url = `${base_url}/app/customer/rica/list`;
    const { data } = await axiosAuthInstance.post(url);
    return data;
  } catch (ex) {
    return createApiError(ex, "getDistributorCustomersList");
  }
};

export const getDistributorDetails = async (i_customer) => {
  try {
    const url = `${base_url}/app/distributor`;
    const response = await axiosAuthInstance.post(url, { distributor_i_customer: i_customer });
    // Fixes MEGSAPP-PORTAL-31: Check response exists before destructuring
    if (!response || !response.data) {
      logger.error("getDistributorDetails: Invalid response", null, { response });
      return createApiError(new Error("Invalid API response"), "getDistributorDetails", { i_customer });
    }
    return response.data;
  } catch (ex) {
    return createApiError(ex, "getDistributorDetails", { i_customer });
  }
};

export const getDistributorList = async () => {
  try {
    const url = `${base_url}/app/distributor/list`;
    const { data } = await axiosAuthInstance.post(url);
    return data;
  } catch (ex) {
    return createApiError(ex, "getDistributorList");
  }
};

export const getSubDistributorList = async (i_customer) => {
  try {
    const url = `${base_url}/app/subDistributor/list`;
    const { data } = await axiosAuthInstance.post(url, { i_customer });
    return data;
  } catch (ex) {
    return createApiError(ex, "getSubDistributorList", { i_customer });
  }
};

export const getSubDistributor = async (i_customer, sub_distributor_id) => {
  try {
    const url = `${base_url}/app/subDistributor?distributor_i_customer=${i_customer}&sub_distributor_id=${sub_distributor_id}`;
    const { data } = await axiosAuthInstance.get(url);
    return data;
  } catch (ex) {
    return createApiError(ex, "getSubDistributor", { i_customer, sub_distributor_id });
  }
};

export const getSchoolByEMIS = async (emis) => {
  try {
    const url = `${base_url}/app/distributor/schools/get-school`;
    const { data } = await axiosApiInstance.post(url, { emis });
    return data;
  } catch (ex) {
    return createApiError(ex, "getSchoolByEMIS", { emis });
  }
};

export const fetchSalesReport = async (external_id, start_date, end_date) => {
  try {
    const url = `${base_url}/distributor/honey/sales`;
    const post_data = {
      customer_number: external_id,
      start_date,
      end_date,
    };
    const { data } = await axiosAuthInstance.post(url, post_data);
    return data;
  } catch (ex) {
    return createApiError(ex, "fetchSalesReport", { external_id, start_date, end_date });
  }
};

export const downloadSalesReport = async (external_id, start_date, end_date) => {
  const config = {
    headers: {
      "Content-Type": "application/json",
    },
    responseType: "blob",
  };
  const download_url = `${base_url}/distributor/honey/report/${external_id}/${start_date}/${end_date}`;

  try {
    const data = await axiosAuthInstance.get(`${download_url}`, config);

    const url = window.URL.createObjectURL(new Blob([data.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${external_id}_${start_date}_${end_date}_sales_report.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);

    return data;
  } catch (ex) {
    return createApiError(ex, "downloadSalesReport", { external_id, start_date, end_date });
  }
};

export const getDistributorReportPeriods = async (distributor_i_customer) => {
  try {
    const url = `${base_url}/app/distributor/AllReportPeriod?distributor_i_customer=${distributor_i_customer}`;
    const { data } = await axiosAuthInstance.get(url);
    return data;
  } catch (ex) {
    return createApiError(ex, "getDistributorReportPeriods", { distributor_i_customer });
  }
};

export const getDistributorReportForPeriod = async (distributor, report_period) => {
  let url = "";

  if (distributor?.i_customer === "All" && report_period === "All") {
    url = `${base_url}/app/distributor/reportPeriod`;
  } else if (distributor?.i_customer !== undefined && report_period === "All") {
    url = `${base_url}/app/distributor/reportPeriod?distributor_i_customer=${distributor?.i_customer}`;
  } else if (distributor?.i_customer !== undefined && report_period !== undefined) {
    url = `${base_url}/app/distributor/reportPeriod?distributor_i_customer=${distributor?.i_customer}&report_period=${report_period}`;
  } else {
    url = `${base_url}/app/distributor/reportPeriod`;
  }

  try {
    const { data } = await axiosAuthInstance.get(url);
    return data;
  } catch (ex) {
    return createApiError(ex, "getDistributorReportForPeriod", { distributor_i_customer: distributor?.i_customer, report_period });
  }
};

export const getTupperwareAgentDetails = async (customer_number) => {
  try {
    const url = `${base_url}/app/tupperware_customer_number?tupperware_customer_number=${customer_number}`;
    const { data } = await axiosApiInstance.get(url);
    return data;
  } catch (ex) {
    return createApiError(ex, "getTupperwareAgentDetails", { customer_number });
  }
};

export const addTupperwareAgentDetails = async (
  external_id,
  first_name,
  last_name,
  email_address,
  id_number,
  phone_number,
  whatsapp_msisdn,
  distributor_i_customer
) => {
  try {
    const url = `${base_url}/app/tupperware_agent`;
    const { data } = await axiosApiInstance.post(url, {
      external_id,
      first_name,
      last_name,
      email_address,
      id_number,
      phone_number,
      whatsapp_msisdn: whatsapp_msisdn === "" ? phone_number : whatsapp_msisdn,
      distributor_i_customer,
    });
    return data;
  } catch (ex) {
    return createApiError(ex, "addTupperwareAgentDetails", { external_id, distributor_i_customer });
  }
};
